import React, { useContext, createContext } from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation, useQueryClient } from "react-query";

import { QUERY_KEYS, USER_TYPES } from "../../constants";
import { USER_PROP_TYPES } from "../../types";
import api from "../../api";

const missingUserProvider = "You forgot to wrap your app in <UserProvider>";

export const UserContext = createContext({
  get user() {
    throw new Error(missingUserProvider);
  },
  get error() {
    throw new Error(missingUserProvider);
  },
  get isLoading() {
    throw new Error(missingUserProvider);
  },
});

UserContext.displayName = "UserContext";

export const useUser = () => useContext(UserContext);

const UserProvider = ({ children, defaultUser }) => {
  const client = api();
  const queryClient = useQueryClient();
  const { data: user, error, isLoading, isFetching, isSuccess } = useQuery(
    QUERY_KEYS.USER,
    client.getUser,
    {
      initialData: defaultUser,
      staleTime: Infinity,
      select: (undecoratedUser) => ({
        ...undecoratedUser,
        isAnonymous:
          undecoratedUser.userTypeId === USER_TYPES.ANONYMOUS.id ||
          undecoratedUser.userTypeId === USER_TYPES.ANONYMOUS_UNIVERSE.id,
        isFamily: undecoratedUser.userTypeId === USER_TYPES.FAMILY.id,
        isUnregisteredFamily:
          undecoratedUser.userTypeId === USER_TYPES.UNREGISTERED_FAMILY.id,
        isUnregisteredTeacher:
          undecoratedUser.userTypeId === USER_TYPES.UNREGISTERED_TEACHER.id,
        isClassroomTeacher:
          undecoratedUser.userTypeId === USER_TYPES.CLASSROOM_TEACHER.id,
        isUserRegistrationEnabled: !!undecoratedUser.features?.registration,
      }),
    },
  );

  const { mutate: updateUser, isLoading: isUpdating } = useMutation(
    (newUserProperties) => client.updateUser(newUserProperties),
    {
      onSettled: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USER);
      },
    },
  );

  return (
    <UserContext.Provider
      value={{
        user,
        updateUser,
        error,
        isLoading,
        isFetching,
        isSuccess,
        isUpdating,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  defaultUser: PropTypes.shape(USER_PROP_TYPES),
};

UserProvider.defaultProps = {
  children: undefined,
  defaultUser: undefined,
};

export default UserProvider;
